import { ModelBase } from '../../core/model/model-base';
import {
  InvoiceAttestStatus,
  InvoiceAttestStatusToString,
  InvoiceDeliveryMethod,
  InvoiceStatus,
  InvoiceType,
  InvoiceTypeToString,
} from './const/enum';
import { InvoiceRow } from './invoice-row';
import { InvoiceDelivery } from './invoice-delivery';
import { InvoiceTransaction } from './invoice-transaction';
import { InvoiceReminder } from './invoice-reminder';
import { InvoiceAccounting, InvoiceAccountingBalance } from './invoice-accounting';
import { LocalDate } from '../../core/model/local-date';
import { Contract } from '../../rent/model/contract';
import { CreditInvoice } from './credit-invoice';
import { DirectDebit } from './direct-debit';
import { CurrencySymbol, Money } from '../../core/model/money';
import { DebtCollectionLog } from './debt-collection-log';
import { B2CInvoiceImportEvent } from './b2c/b2c-invoice-import-event';
import { InvoiceDocument } from './invoice-document';

export class InvoiceRowsChangeModel {
  changeHappened: boolean;
  amountAndTextRows: InvoiceRow[];
  changedInvoiceRowsIds: string[];
}

export class Invoice extends ModelBase {
  constructor() {
    super();
    this._accounting = [];
    this.debtor = {};
    this._rows = [];
    this._amountAndTextRows = [];
    this._deliveries = [];
    this._b2CInvoiceImportEvents = [];
    this._transactions = [];
    this._reminders = [];
    this.leaseAgreements = [];
    this.creditInvoices = [];
    this.appendixes = [];

    Object.defineProperties(this, {
      _accounting: { enumerable: false, writable: true },
      _defermentDate: { enumerable: false, writable: true },
      _dueDate: { enumerable: false, writable: true },
      _fullyRegulated: { enumerable: false, writable: true },
      _sentToDebtCollection: { enumerable: false, writable: true },
      _dueDateString: { enumerable: false, writable: false },
      _invoiceDate: { enumerable: false, writable: true },
      _attestedDate: { enumerable: false, writable: true },
      _periodFrom: { enumerable: false, writable: true },
      _periodTo: { enumerable: false, writable: true },
      _rows: { enumerable: false, writable: true },
      _amountAndTextRows: { enumerable: false, writable: true },
      _deliveries: { enumerable: false, writable: true },
      _b2CInvoiceImportEvents: { enumerable: false, writable: true },
      _transactions: { enumerable: false, writable: true },
      _reminders: { enumerable: false, writable: true },
    });
  }

  private _accounting: InvoiceAccounting[];
  private _defermentDate: LocalDate;
  private _dueDate: LocalDate;
  private _fullyRegulated: LocalDate;
  private _sentToDebtCollection: LocalDate;
  private _invoiceDate: LocalDate;
  private _attestedDate: LocalDate;
  private _periodFrom: LocalDate;
  private _periodTo: LocalDate;
  private _rows: InvoiceRow[];
  private _deliveries: InvoiceDelivery[];
  private _b2CInvoiceImportEvents: B2CInvoiceImportEvent[];
  private _transactions: InvoiceTransaction[];
  private _reminders: InvoiceReminder[];

  amount: Money;
  companyId: string;
  companyName: string;
  companyNumberAndName: string;
  currencyCode: string;
  invoiceDescription: string;
  netAmount: Money;
  vatAmount: Money;
  rounding: Money;
  debtor: any;
  deliveryMethod: InvoiceDeliveryMethod;
  deliveryMethods: InvoiceDeliveryMethod[];
  typeOfInvoice: InvoiceType;
  allowEditInvoiceRows: boolean;
  invoiceRowsChangeModel: InvoiceRowsChangeModel;

  get deliveries(): InvoiceDelivery[] {
    return this._deliveries;
  }
  set deliveries(ditems: InvoiceDelivery[]) {
    if (ditems) {
      ditems.forEach(ditem => {
        if (ditem instanceof InvoiceDelivery) {
          this._deliveries.push(ditem);
        } else {
          this._deliveries.push(Object.assign(new InvoiceDelivery(), ditem));
        }
      });
    }
  }

  get b2CInvoiceImportEvents(): B2CInvoiceImportEvent[] {
    return this._b2CInvoiceImportEvents;
  }
  set b2CInvoiceImportEvents(b2citems: B2CInvoiceImportEvent[]) {
    if (b2citems) {
      b2citems.forEach(b2citem => {
        if (b2citem instanceof B2CInvoiceImportEvent) {
          this._b2CInvoiceImportEvents.push(b2citem);
        } else {
          this._b2CInvoiceImportEvents.push(Object.assign(new B2CInvoiceImportEvent(), b2citem));
        }
      });
    }
  }

  get accountingTransactions(): InvoiceAccounting[] {
    return this._accounting;
  }
  set accountingTransactions(accountingItems: InvoiceAccounting[]) {
    if (accountingItems) {
      accountingItems.forEach(accountingItem => {
        if (accountingItem instanceof InvoiceAccounting) {
          this._accounting.push(accountingItem);
        } else {
          this._accounting.push(Object.assign(new InvoiceAccounting(), accountingItem));
        }
      });
    }
  }

  accountingBalances: InvoiceAccountingBalance[];

  get transactions(): InvoiceTransaction[] {
    return this._transactions;
  }
  set transactions(titems: InvoiceTransaction[]) {
    if (titems) {
      titems.forEach(titem => {
        if (titem instanceof InvoiceTransaction) {
          this._transactions.push(titem);
        } else {
          this._transactions.push(Object.assign(new InvoiceTransaction(), titem));
        }
      });
    }
  }

  get reminders(): InvoiceReminder[] {
    return this._reminders;
  }
  set reminders(reminderItems: InvoiceReminder[]) {
    if (reminderItems) {
      reminderItems.forEach(reminderItem => {
        if (reminderItem instanceof InvoiceReminder) {
          this._reminders.push(reminderItem);
        } else {
          this._reminders.push(Object.assign(new InvoiceReminder(), reminderItem));
        }
      });
    }
  }

  leaseAgreements: Contract[];
  directDebits: DirectDebit[];
  debtCollectionLogs: DebtCollectionLog[];
  creditInvoices: CreditInvoice[];
  get hasCreditInvoices(): boolean {
    return this.creditInvoices && this.creditInvoices.length > 0;
  }

  lastSentReminder: Date;

  invoiceContacts: any[];
  invoiceDeliveryMethodText: string;

  get defermentDate(): LocalDate {
    return this._defermentDate;
  }
  set defermentDate(value: LocalDate) {
    this._defermentDate = LocalDate.fromDate(value);
  }
  get fullyRegulated(): LocalDate {
    return this._fullyRegulated;
  }
  set fullyRegulated(value: LocalDate) {
    this._fullyRegulated = LocalDate.fromDate(value);
  }
  get sentToDebtCollection(): LocalDate {
    return this._sentToDebtCollection;
  }
  set sentToDebtCollection(value: LocalDate) {
    this._sentToDebtCollection = LocalDate.fromDate(value);
  }
  get displayName(): string {
    return this.invoiceNumber;
  }
  get dueDate(): LocalDate {
    return this._dueDate;
  }
  set dueDate(value: LocalDate) {
    this._dueDate = LocalDate.fromDate(value);
  }
  get dueDateString(): String {
    return this.dateToString(this.dueDate.toDate(), 'sv-SE');
  }

  errorMessage: string;
  get invoiceDate(): LocalDate {
    return this._invoiceDate;
  }
  set invoiceDate(value: LocalDate) {
    this._invoiceDate = LocalDate.fromDate(value);
  }
  get attestedDate(): LocalDate {
    return this._attestedDate;
  }
  set attestedDate(value: LocalDate) {
    this._attestedDate = LocalDate.fromDate(value);
  }
  invoiceNumber: string;
  bankGiroNumber: string;
  ocrNumber: string;
  invoiceRecipient: string;
  invoiceRecipientEmail: string;
  labeling: string;
  note: string;
  directDebitInvoice: string;

  get isAttested(): boolean {
    return this.status === InvoiceStatus.Attested;
  }
  get isDeliveryMethodEmail(): boolean {
    return this.deliveryMethod === InvoiceDeliveryMethod.EmailWithPDF || this.deliveryMethod === InvoiceDeliveryMethod.EmailWithoutPDF;
  }
  get periodFrom(): LocalDate {
    return this._periodFrom;
  }
  set periodFrom(value: LocalDate) {
    this._periodFrom = LocalDate.fromDate(value);
  }
  get periodTo(): LocalDate {
    return this._periodTo;
  }
  set periodTo(value: LocalDate) {
    this._periodTo = LocalDate.fromDate(value);
  }
  get isCreditInvoice(): boolean {
    return this.amount.amount < 0;
  }
  remainingAmount: Money;
  unpaidInformationAmount: Money;
  refersToPeriod: string;
  invoiceRecipientContactId: string;
  get rows(): InvoiceRow[] {
    return this._rows;
  }
  set rows(items: InvoiceRow[]) {
    if (items) {
      items.forEach(item => {
        if (item instanceof InvoiceRow) {
          this._rows.push(item);
        } else {
          this._rows.push(new InvoiceRow(item));
        }
      });
    }
  }

  private _amountAndTextRows: InvoiceRow[];
  get amountAndTextRows(): InvoiceRow[] {
    return this._amountAndTextRows;
  }
  set amountAndTextRows(items: InvoiceRow[]) {
    this._amountAndTextRows = items;
  }

  paymentLateNumberOfDays: number;
  emailDeliveryInfo: string;
  emailDeliveryStatusText: string;

  status: InvoiceStatus;
  attestStatus: InvoiceAttestStatus;

  get statusCaption(): string {
    return InvoiceAttestStatusToString(this.attestStatus);
  }
  usedDeliveryMethods: InvoiceDeliveryMethod[];
  appendixes: InvoiceDocument[];
  newAppendixes: File[];

  get invoiceTypeText(): string {
    return InvoiceTypeToString(this.typeOfInvoice);
  }

  toJSON() {
    return {
      debtorEmailAddress: this.debtor.email,
      deliveryMethod: this.deliveryMethod,
      dueDate: this.dueDate,
      invoiceDate: this.invoiceDate,
      id: this.id,
      recipientAddress: this.debtor.address,
      attestedDate: this.attestedDate,
      sentToDebtCollection: this.sentToDebtCollection,
      currencyCode: this.currencyCode,
      labeling: this.labeling,
      note: this.note,
      invoiceRowsChangeModel: this.invoiceRowsChangeModel,
      appendixes: this.newAppendixes,
    };
  }

  toString(culture?: string): string {
    let dueDateString = culture ? this.dateToString(this.dueDate.toDate(), culture) : this.dueDateString;

    return `${this.invoiceNumber ? this.invoiceNumber + ' ' : ''}${
      this.invoiceRecipient ? this.invoiceRecipient + ' ' : ''
    }${dueDateString} ${this.remainingAmount.amount} ${CurrencySymbol(this.remainingAmount.currencyCode)}`;
  }
}

export enum InvoiceCategory {
  Any,
  Attested,
  FullyPaid,
  NotAttested,
  Overdue,
  PartiallyPaid,
  Unpaid,
}
